import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

const blank = () => ({ subject: '', customer_reference: '', received_at: '', target_minutes: '240' });
const when = value => new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
const errorText = err => typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Unable to complete this request. Refresh the list before trying again.';

export default function CustomerResponsePanel() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [more, setMore] = useState(false);
  const [listingReady, setListingReady] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const requestId = useRef(null);
  const actionId = useRef(null);
  const locked = useRef(false);
  const [uncertain, setUncertain] = useState(false);
  const [success, setSuccess] = useState('');

  const refresh = async () => {
    setError('');
    setListingReady(false);
    try {
      const { data: cfg } = await api.get('/customer-response/status');
      setStatus(cfg);
      if (cfg.enabled) {
        const { data } = await api.get('/customer-response');
        setItems(data.items); setMore(data.has_more);
        setListingReady(true);
      }
    } catch (err) { setError(errorText(err)); }
  };
  useEffect(() => { refresh(); }, []);
  const save = async event => {
    event.preventDefault();
    if (locked.current) return;
    locked.current = true; setBusy(true); setError(''); setSuccess('');
    try {
      if (!requestId.current) requestId.current = crypto.randomUUID();
      await api.post('/customer-response', { request_id: requestId.current, item: {
        ...form, received_at: new Date(form.received_at).toISOString(), target_minutes: Number(form.target_minutes),
      }});
      requestId.current = null; setUncertain(false); setForm(blank());
      setSuccess('Response record saved. No email was sent.'); await refresh();
    } catch (err) {
      const rejected = [400, 403, 422].includes(err?.response?.status);
      setUncertain(!rejected);
      if (rejected) requestId.current = null;
      setError(`${errorText(err)}${rejected ? '' : ' Your save identity is retained; retry uses the same identity.'}`);
    } finally { locked.current = false; setBusy(false); }
  };
  const recordOutcome = async event => {
    event.preventDefault();
    if (locked.current) return;
    locked.current = true; setBusy(true); setError(''); setSuccess('');
    try {
      if (!actionId.current) actionId.current = crypto.randomUUID();
      await api.post(`/customer-response/${editor.item.id}/outcome`, {
        action_id: actionId.current, version: editor.item.version, state: editor.state, note, confirmed,
      });
      setEditor(null); actionId.current = null; setUncertain(false);
      setSuccess('Outcome recorded as a staff confirmation. No email was sent.'); await refresh();
    } catch (err) { setUncertain(true); setError(errorText(err)); }
    finally { locked.current = false; setBusy(false); }
  };
  const editOutcome = (item, state) => {
    setEditor({item, state}); setNote(''); setConfirmed(false); actionId.current = null;
  };
  const field = (name, label, type = 'text') => <label className="text-sm block">{label}
    <input required type={type} maxLength={name === 'subject' ? 160 : 120} value={form[name]}
      onChange={e => setForm({ ...form, [name]: e.target.value })} disabled={busy || uncertain}
      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2" /></label>;
  return <section className="space-y-5" aria-label="Customer response tracking">
    <div><h2 className="font-semibold text-slate-900">Customer response tracking</h2>
      <p className="text-sm text-slate-600 mt-1">Give each customer enquiry an owner and an internal response target.</p></div>
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-slate-700">
      <strong>Staff-recorded tracking.</strong> Outlook and Gmail replies are not monitored here. This panel never sends email or changes an order.
      <p className="mt-1">Targets use Monday–Friday, 09:00–18:00 IST. Holidays are not configured in this first interface. Targets are internal commitments, not contractual SLAs.</p>
    </div>
    {error && <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {success && <p role="status" className="text-sm text-emerald-800">{success}</p>}
    {!status && !error && <p>Loading response tracking…</p>}
    {status && !status.enabled && <div className="rounded-lg border p-5"><strong>Not activated</strong>
      <p className="text-sm text-slate-600 mt-2">This feature requires a reviewed release and server activation. Existing communications continue through their current workflow.</p></div>}
    {status?.enabled && !listingReady && !error && <p>Loading response records…</p>}
    {status?.enabled && listingReady && <>
      {status.can_write && <form onSubmit={save} className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
        <h3 className="font-medium">Record an enquiry</h3>
        <div className="grid sm:grid-cols-2 gap-4">{field('customer_reference','Customer / CRM reference')}{field('subject','Enquiry summary')}
          {field('received_at','Received at (your device’s local time)','datetime-local')}
          <label className="text-sm">Internal response target<select value={form.target_minutes} disabled={busy || uncertain}
            onChange={e => setForm({...form,target_minutes:e.target.value})} className="block mt-1 rounded-md border p-2 w-full">
            <option value="120">2 business hours</option><option value="240">4 business hours</option><option value="540">1 business day (9 hours)</option>
          </select></label></div>
        <p className="text-xs text-slate-500">Use a short reference and summary; do not paste message bodies, passwords or sensitive attachments.</p>
        <Button disabled={busy || Boolean(editor)} type="submit">{busy ? 'Saving…' : uncertain ? 'Retry same save' : 'Save response record'}</Button>
      </form>}
      <div className="flex items-center justify-between"><h3 className="font-medium">Response records</h3><Button variant="outline" disabled={busy} onClick={refresh}>Refresh</Button></div>
      <p className="text-xs text-slate-500">Staff see their own records; management can see the team’s records. Showing {items.length}{more ? ' most recent records; older records are not shown' : ' records'}. Times below are IST.</p>
      {!items.length && <p className="rounded-lg border border-dashed p-6 text-sm text-slate-500">No response records are available in your view.</p>}
      {items.map(item => <article key={`${item.owner}:${item.id}`} className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap gap-2 justify-between"><h4 className="font-semibold">{item.subject}</h4>
          <span className={`text-xs rounded-full px-3 py-1 ${item.status === 'OVERDUE' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{item.status.replaceAll('_',' ')}</span></div>
        <p className="text-sm mt-1">{item.customer_reference}</p><p className="text-xs text-slate-500 mt-2">Owner: {item.owner} · Due: {when(item.due_at)}</p>
        {item.state === 'OPEN' && status.can_write && <div className="flex gap-2 mt-3">
          <Button variant="outline" disabled={busy || uncertain || Boolean(editor)} onClick={() => editOutcome(item,'REPLIED')}>Record reply already sent</Button>
          <Button variant="outline" disabled={busy || uncertain || Boolean(editor)} onClick={() => editOutcome(item,'NO_REPLY_REQUIRED')}>No reply required</Button></div>}
        {item.state !== 'OPEN' && <p className="text-xs text-slate-500 mt-2">Staff-confirmed by {item.recorded_by} · {when(item.recorded_at)}</p>}
      </article>)}
      {editor && <form onSubmit={recordOutcome} className="rounded-lg border border-slate-300 p-4 space-y-3" aria-label="Confirm response outcome">
        <h3 className="font-medium">{editor.state === 'REPLIED' ? 'Confirm a reply was already sent' : 'Confirm no reply is required'}</h3>
        <p className="text-sm">{editor.item.subject}. This records your confirmation; it does not send a response or verify mailbox delivery.</p>
        <label className="block text-sm">Short audit note<input required minLength={5} maxLength={300} value={note} disabled={busy || uncertain} onChange={e => setNote(e.target.value)} className="block w-full border rounded-md p-2 mt-1" /></label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmed} disabled={busy || uncertain} onChange={e => setConfirmed(e.target.checked)} />I have checked the enquiry and confirm this outcome.</label>
        <div className="flex gap-2"><Button type="submit" disabled={busy || !confirmed}>{uncertain ? 'Retry same confirmation' : 'Record outcome'}</Button>
          <Button type="button" variant="outline" disabled={busy || uncertain} onClick={() => setEditor(null)}>Cancel</Button></div>
      </form>}
    </>}
  </section>;
}
